import React, { useEffect, useMemo, useState } from "react";
import { Collapse, Tag, Badge, Button, Spin, Input, Select } from "antd";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as actions from "redux/Dashboard/actions.js";
import { useTranslation } from "react-i18next";
import DropdownSelect from "helperComponents/CustomDropdown";

const formatCount = (num) => {
  if (num == null || Number.isNaN(num)) return "0";
  return new Intl.NumberFormat("tr-TR").format(num);
};

const filterTopicsBySearch = (topics, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return topics;
  return topics.reduce((acc, item) => {
    const childMatches = item.child?.length
      ? filterTopicsBySearch(item.child, q)
      : [];
    const selfMatch = item.name?.toLowerCase().includes(q);
    if (selfMatch || childMatches.length > 0) {
      acc.push({
        ...item,
        child: childMatches.length > 0 ? childMatches : item.child,
      });
    }
    return acc;
  }, []);
};

const sortTopics = (topics, sortKey) => {
  const sorted = [...topics].sort((a, b) => {
    if (sortKey === "alpha") {
      return (a.name || "").localeCompare(b.name || "", "tr");
    }
    return (b.count || 0) - (a.count || 0);
  });
  return sorted.map((item) =>
    item.child?.length
      ? { ...item, child: sortTopics(item.child, sortKey) }
      : item
  );
};

const flattenTopics = (topics, depth = 0) => {
  return topics.flatMap((item) => {
    const row = { ...item, depth };
    if (item.child?.length) {
      return [row, ...flattenTopics(item.child, depth + 1)];
    }
    return [row];
  });
};

const countFilterTopics = (topics) => {
  if (!topics?.length) return 0;

  const withoutParentType = topics.filter((topic) => topic.type !== "parent");
  const parentIdsWithSelectedChild = new Set(
    withoutParentType
      .map((topic) => topic.parent_id)
      .filter((id) => id != null)
  );

  return withoutParentType.filter((topic) => {
    if (!parentIdsWithSelectedChild.has(topic.id)) {
      return true;
    }
    return !withoutParentType.some(
      (child) => child.parent_id === topic.id && child.id !== topic.id
    );
  }).length;
};

const TopTopics = ({
  data,
  disableTopTopicsBtn,
  applyDisabled = false,
  selectedTopTopics,
  setSelectedTopTopics,
  topTopicsLoading,
  checkedTopicIds,
  setCheckedTopicIds,
  selectedTopicIds,
  setSelectedTopicIds,
  boardType,
  handleClearAll,
  handleFilterClear,
  setTopicsOperator,
  showOnlyTree,
  setSavedSearchShowTopTopics,
  setSavedSearchTopTopicsFilter,
  setSavedSearchChildOthers,
  setSavedSearchParentOthers,
  applyPopupStyling,
}) => {
  const [type, setType] = useState("all");
  const [viewType, setViewType] = useState("category");
  const [showHierarchy, setShowHierarchy] = useState(true);
  const [words, setWords] = useState();
  const [startIndex, setStartIndex] = useState(0);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const DashboardReducer = useSelector((state) => state?.DashboardReducer);
  const [searchValue, setSearchValue] = useState("");
  const [baseCategoryList, setBaseCategoryList] = useState([]);
  const [trigger, setTrigger] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedFilterType, setSelectedFilterType] = useState(null);
  const parentBoxRef = React.useRef(null);
  const { search } = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const [queryFilters, setQueryFilters] = useState({});
  const [childOthers, setChildOthers] = useState([]);
  const [parentOthers, setParentOthers] = useState([]);
  const [discardedAITopics, setDiscardedAITopics] = useState([]);
  const [sortKey, setSortKey] = useState("volume");
  const [appliedSnapshot, setAppliedSnapshot] = useState({
    source: "all",
    view: "hierarchy",
    sort: "volume",
    selected: [],
    selectedTopics: [],
  });

  useEffect(() => {
    if (params) {
      const values = Array.from(params);
      const queryObj = values.reduce((acc, [key, val]) => {
        if (
          key === "keyword" ||
          key === "pivotFilter" ||
          key === "top_topics" ||
          key === "level1Selection" ||
          key === "level2Selection" ||
          key === "level1OfSelected" ||
          key === "level2OfSelected" ||
          key === "decisionFilter" ||
          key === "child_others" ||
          key === "parent_others" ||
          key === "hotterm"
        ) {
          acc[key] = JSON.parse(val);
        } else {
          acc[key] = val;
        }
        return acc;
      }, {});
      setQueryFilters(queryObj);
    }
  }, [history, search]);

  const searchTopics = (topicsArr, id, id_col) => {
    if (!topicsArr || topicsArr?.length === 0) return null;
    if (id === null || id === undefined || id === "") return null;
    const needle = Number(id);
    if (!Number.isFinite(needle)) return null;
    for (let tpc of topicsArr) {
      // Coerce both sides: API tree fields may be string or number.
      if (Number(tpc[id_col]) === needle) {
        return tpc;
      }
      if (tpc.child && tpc.child.length > 0) {
        const res = searchTopics(tpc.child, id, id_col);
        if (res) {
          return res;
        }
      }
    }
    return null;
  };

  // URL/API custom topics use topicId; checkbox selection uses lib_id.
  const toCheckboxTopic = (topic) => {
    if (!topic || topic.type !== "custom") {
      return { ...topic };
    }
    const byTopicId = searchTopics(data?.tree, topic.id, "id");
    if (byTopicId?.lib_id != null) {
      return { ...topic, id: Number(byTopicId.lib_id) };
    }
    // Already a lib_id (or tree not ready) — keep as-is, never write undefined.
    const byLibId = searchTopics(data?.tree, topic.id, "lib_id");
    if (byLibId?.lib_id != null) {
      return { ...topic, id: Number(byLibId.lib_id) };
    }
    return { ...topic };
  };

  const toApiTopic = (topic) => {
    if (!topic || topic.type !== "custom") {
      return { ...topic };
    }
    const byLibId = searchTopics(data?.tree, topic.id, "lib_id");
    if (byLibId?.id != null) {
      return { ...topic, id: Number(byLibId.id) };
    }
    // Already a topicId — keep it if present in tree, otherwise keep original id.
    const byTopicId = searchTopics(data?.tree, topic.id, "id");
    if (byTopicId?.id != null) {
      return { ...topic, id: Number(byTopicId.id) };
    }
    if (topic.id == null) {
      return null;
    }
    return { ...topic, id: Number(topic.id) };
  };

  useEffect(() => {
    if (
      topTopicsLoading ||
      !data?.tree?.length ||
      !queryFilters?.top_topics ||
      queryFilters.top_topics.length === 0
    ) {
      return;
    }
    // Clone — never mutate queryFilters.top_topics in place (shared with API payloads).
    const topics = queryFilters.top_topics.map(toCheckboxTopic);
    setSelectedTopicIds(topics);
    setCheckedTopicIds(topics);
    setAppliedSnapshot((prev) => ({
      ...prev,
      selected: topics.map((topic) => topic.id),
      selectedTopics: topics,
    }));
  }, [queryFilters.top_topics, topTopicsLoading, data?.tree]);

  const addTopics = (id, type, label) => {
    let filterID = queryFilters?.top_topics?.filter((item) => item.id === id);
    if (!filterID || filterID?.length === 0) {
      params.delete("top_topics");
      params.append(
        "top_topics",
        JSON.stringify(
          queryFilters?.top_topics?.length > 0
            ? [
                ...queryFilters?.top_topics,
                { id: id, type: type, label: label },
              ]
            : [{ id: id, type: type, label: label }]
        )
      );
      params.append("topics_operator", "AND");
      history.push({ search: params.toString() });
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenTopTopic: [
            ...DashboardReducer.searchState.chosenTopTopic,
            { id: id, type: type, label: label },
          ],
        },
      });
    } else {
      params.delete("top_topics");
      params.append(
        "top_topics",
        JSON.stringify(
          queryFilters?.top_topics?.filter((item) => item.id !== id)
        )
      );
      params.append("topics_operator", "AND");
      history.push({ search: params.toString() });
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenTopTopic: DashboardReducer.searchState.chosenTopTopic?.filter(
            (item) => item.id !== id
          ),
        },
      });
    }
  };

  const removeAITopics = (tree) => {
    if (tree?.length > 0) {
      const newTree = tree.reduce((acc, item) => {
        if (item.type !== 1) {
          if (item.child && item.child.length > 0) {
            item.child = removeAITopics(item.child);
          }
          acc.push(item);
        }
        return acc;
      }, []);
      return newTree;
    } else {
      return [];
    }
  };

  useEffect(() => {
    setStartIndex(0);
    if (type === "all") {
      setWords(data?.list?.filter((item) => item.type === "custom"));
      setBaseCategoryList(data?.tree || []);
      setDiscardedAITopics(data?.discarded_topics);
      setViewType("category");
    } else if (type === "ai") {
      const aiTpcs = data?.list?.filter((item) => item.type === "ai");
      setWords(aiTpcs);
      setBaseCategoryList(
        aiTpcs?.map((aiTopic) => ({
          id: aiTopic?.id,
          name: aiTopic?.label,
          learning: false,
          child: [],
          count: aiTopic?.count,
          parent_id: null,
          type: 1,
        })) || []
      );
      setDiscardedAITopics(data?.discarded_topics);
      setViewType("category");
    } else if (type === "fixed") {
      setWords(data?.list?.filter((item) => item.type === "fixed"));
      setViewType("plain");
    } else if (type === "custom") {
      setWords(data?.list?.filter((item) => item.type === "custom"));
      setBaseCategoryList(removeAITopics(data?.tree) || []);
      setDiscardedAITopics(data?.discarded_topics);
      setViewType("category");
    }
  }, [type, data]);

  const visibleCategoryList = useMemo(() => {
    let list = baseCategoryList || [];
    list = filterTopicsBySearch(list, searchValue);
    list = sortTopics(list, sortKey);
    return list;
  }, [baseCategoryList, searchValue, sortKey]);

  const topicsMatchAppliedFilter = (topics) => {
    const normalize = (items) =>
      JSON.stringify(
        (items || [])
          .map((item) => {
            const apiTopic = toApiTopic(item);
            const id = apiTopic?.id ?? item?.id;
            return `${id}:${item.type}:${item.parent_id ?? ""}`;
          })
          .sort()
      );
    return normalize(topics) === normalize(queryFilters?.top_topics);
  };

  const filterTopicCount = useMemo(
    () => countFilterTopics(selectedTopicIds),
    [selectedTopicIds]
  );

  const needsOperatorChoice = filterTopicCount >= 2;

  const selectionPendingMessage = (() => {
    if (filterTopicCount === 0) {
      return t("top_topics.pendingFilterClear");
    }
    if (filterTopicCount === 1) {
      return t("top_topics.pendingFilterSingle");
    }
    return t("top_topics.pendingFilterMultiple", {
      count: filterTopicCount,
    });
  })();

  const hasSelectionPending = !topicsMatchAppliedFilter(selectedTopicIds);

  const applyOptions = [
    {
      id: "1",
      label: t("top_topics.applyIntersection"),
      value: "AND",
    },
    {
      id: "2",
      label: t("top_topics.applyUnion"),
      value: "OR",
    },
  ];

  const numFormatter = (num) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * Math.trunc(Math.abs(num) / 1000) + "K"
      : num;
  };

  const visualWords = words?.map((word, index) => {
    if (index >= 10 * startIndex && index < 10 * (startIndex + 1)) {
      return (
        <Tag
          icon={
            <Badge
              count={numFormatter(word.count)}
              overflowCount={9999999999}
              style={{
                marginRight: "10px",
                background: "#FFFFFF",
                border: "none",
                color: "#4400AA",
              }}
            />
          }
          className={
            DashboardReducer?.searchState?.chosenTopTopic?.some(
              (item) => item.id === word.id
            )
              ? styles.activeTerm
              : ""
          }
          color={theme === "brand" ? "#4a4d4e" : "#4400AA"}
          onClick={() =>
            !disableTopTopicsBtn && addTopics(word.id, word.type, word.label)
          }
          title={word.label}
        >
          <span className={styles.wordLabel}>{word.label}</span>
        </Tag>
      );
    }
  });

  const updatePage = (increment) => {
    setStartIndex(startIndex + increment);
  };

  const selectionStatus = (id) => {
    let parent = baseCategoryList?.find((item) => item?.lib_id === id);
    let fullSelected = true;
    let partialSelected = false;
    let noneSelected = true;
    for (let i = 0; i < parent?.child?.length; i++) {
      let c = parent?.child[i];
      if (c && c.count) {
        if (checkedTopicIds?.some((item) => item.id === c.lib_id)) {
          noneSelected = false;
          partialSelected = true;
        } else {
          fullSelected = false;
        }
      }
      if (c?.child?.length !== 0) {
        let status = selectionStatus(c.lib_id);
        if (status === "all") {
          noneSelected = false;
          partialSelected = true;
        } else if (status === "some") {
          noneSelected = false;
          partialSelected = true;
          fullSelected = false;
        } else {
          fullSelected = false;
        }
      }
    }

    if (fullSelected) return "all";
    if (partialSelected) return "some";
    if (noneSelected) return "none";
  };

  const selectNestedTopics = (parentId) => {
    let arr = [];
    let parent = findCategoryParent(baseCategoryList, parentId);
    if (parent?.child?.length === 0) return [];
    for (let i = 0; i < parent?.child?.length; i++) {
      let c = parent?.child[i];
      const item = {
        label: c.name,
        id: c.type === 1 ? parseInt(c.id, 10) : parseInt(c.lib_id, 10),
        type: c.type === 1 ? "ai" : "custom",
      };
      if (c.type === "custom") {
        item.parent_id = parentId;
      }
      arr.push(item);
      if (c?.child?.length !== 0) {
        arr = arr.concat(selectNestedTopics(c.id));
      }
    }

    return arr;
  };

  const findCategoryParent = (arr, value) => {
    for (const item of arr) {
      if (item.lib_id === parseInt(value, 10)) {
        return item;
      }
      if (item.child) {
        const found = findCategoryParent(item.child, value);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const getAllNestedTopicColsOfParent = (
    topicsArr,
    parentId,
    colName = "id",
    values = []
  ) => {
    const topicValues = values;
    topicsArr.forEach((tpc) => {
      if (tpc.child?.length > 0) {
        if (tpc.lib_id === parentId) {
          topicValues.push(...tpc.child.map((item) => item));
        }
        topicValues.push(
          ...getAllNestedTopicColsOfParent(
            tpc.child,
            parentId,
            colName,
            topicValues
          )
        );
      }
    });
    return [...new Set(topicValues.filter((item) => item?.id > 0))];
  };

  const handleCheckboxChange = (e) => {
    if (disableTopTopicsBtn) {
      return;
    }
    const { id, name, value, accessKey, itemType } = e.target;
    if (value) {
      if (
        selectedTopicIds?.filter((item) => {
          if (parseInt(itemType, 10) === 1) {
            return item.id === parseInt(accessKey, 10);
          } else {
            return item.id === parseInt(id, 10);
          }
        })?.length !== 0
      ) {
        const parent = findCategoryParent(baseCategoryList, parseInt(value, 10));
        const newArr = selectedTopicIds.filter(
          (item) =>
            ![parseInt(id, 10), parseInt(accessKey, 10)].includes(item.id)
        );
        const checkedTopicIdsOfParent = [
          ...selectNestedTopics(parseInt(id, 10)),
        ]?.map((item) => item.id);
        const newChecked = checkedTopicIds.filter(
          (item) =>
            ![
              ...checkedTopicIdsOfParent,
              parseInt(id, 10),
              parseInt(accessKey, 10),
            ].includes(item.id)
        );
        if (
          parent &&
          !newArr.some((item) => item.parent_id === parent.lib_id)
        ) {
          setSelectedTopicIds(
            newArr.filter(
              (item) =>
                ![parent.lib_id, ...checkedTopicIdsOfParent].includes(item.id)
            )
          );
          setCheckedTopicIds(
            newChecked.filter((item) => item.id !== parent.lib_id)
          );
        } else {
          setSelectedTopicIds(newArr);
          setCheckedTopicIds(newChecked);
        }
      } else {
        const newArr = [...selectedTopicIds];
        const newCheckedArr = [...checkedTopicIds];
        const parent = selectedTopicIds.find(
          (item) =>
            item.id ===
            selectedTopicIds.find((item) => {
              if (parseInt(itemType, 10) === 1) {
                return item.id === parseInt(accessKey, 10);
              } else {
                return item.id === parseInt(id, 10);
              }
            })?.parent_id
        );
        if (parent) {
          const it = {
            label: name,
            id:
              parseInt(itemType) === 1
                ? parseInt(accessKey, 10)
                : parseInt(id, 10),
            type: parseInt(itemType) === 1 ? "ai" : "custom",
          };
          if (parseInt(itemType) !== 1) {
            it.parent_id = parseInt(value, 10);
          }
          newArr.push(it);
          newCheckedArr.push(it);
        } else {
          const itm = {
            label: name,
            id:
              parseInt(itemType) === 1
                ? parseInt(accessKey, 10)
                : parseInt(id, 10),
            type: parseInt(itemType) === 1 ? "ai" : "custom",
          };
          if (parseInt(itemType) !== 1) {
            itm.parent_id = parseInt(value, 10);
          }
          newArr.push(itm);
          newCheckedArr.push(itm);
          const catParent = findCategoryParent(baseCategoryList, value);
          if (parseInt(itemType) !== 1) {
            newArr.push({
              label: catParent?.name,
              id: parseInt(value, 10),
              type: "parent",
            });
            newCheckedArr.push({
              label: catParent?.name,
              id: parseInt(value, 10),
              type: "parent",
            });
          }
        }
        newCheckedArr.push(...selectNestedTopics(parseInt(id, 10)));
        const unique = [
          ...new Map(newArr.map((item) => [item.id, item])).values(),
        ];
        const uniqueChecked = [
          ...new Map(newCheckedArr.map((item) => [item.id, item])).values(),
        ];
        setSelectedTopicIds(unique);
        setCheckedTopicIds(uniqueChecked);
      }
    } else {
      if (
        selectedTopicIds?.filter((item) => {
          if (parseInt(itemType, 10) === 1) {
            return item.id === parseInt(accessKey, 10);
          } else {
            return item.id === parseInt(id, 10);
          }
        })?.length !== 0
      ) {
        const toBeRemoved = selectNestedTopics(parseInt(id, 10));
        const topicArr = selectedTopicIds.filter(
          (item) => !toBeRemoved.find((i) => i.id === item.id)
        );
        const checkArr = checkedTopicIds.filter(
          (item) => !toBeRemoved.find((i) => i.id === item.id)
        );
        const tpcArr = topicArr.filter((item) => {
          if (parseInt(itemType, 10) === 1) {
            return item.id !== parseInt(accessKey, 10);
          } else {
            return item.id !== parseInt(id, 10);
          }
        });
        setSelectedTopicIds(tpcArr);
        setCheckedTopicIds(
          checkArr.filter((item) => {
            if (parseInt(itemType, 10) === 1) {
              return item.id !== parseInt(accessKey, 10);
            } else {
              return item.id !== parseInt(id, 10);
            }
          })
        );
      } else {
        const arr = selectedTopicIds;
        const checkedArr = checkedTopicIds;
        arr.push({
          label: name,
          id:
            parseInt(itemType, 10) === 1
              ? parseInt(accessKey, 10)
              : parseInt(id, 10),
          type: parseInt(itemType, 10) === 1 ? "ai" : "parent",
        });
        checkedArr.push({
          label: name,
          id:
            parseInt(itemType, 10) === 1
              ? parseInt(accessKey, 10)
              : parseInt(id, 10),
          type: parseInt(itemType, 10) === 1 ? "ai" : "parent",
        });
        const unique = [
          ...new Map(arr.map((item) => [item.id, item])).values(),
        ];
        checkedArr.push(...selectNestedTopics(parseInt(id, 10)));
        const uniqueChecked = [
          ...new Map(checkedArr.map((item) => [item.id, item])).values(),
        ];
        setCheckedTopicIds([...uniqueChecked]);
        setSelectedTopicIds([...unique]);
      }
    }
    setTrigger(!trigger);
  };

  useEffect(() => {
    if (selectedTopicIds?.some((item) => item.id === -2)) {
      const selectedAIIds = selectedTopicIds
        .filter((item) => item.type === "ai")
        ?.map((item) => item?.id);
      const discardedAIIds = discardedAITopics?.map(
        (item) => item?.ai_topic_id
      );
      const allAIIds = data?.list
        ?.filter((item) => item?.type === "ai")
        ?.map((item) => item?.id);

      setParentOthers((prev) => {
        const ai = [...allAIIds, ...prev].filter(
          (item) => ![...selectedAIIds, ...discardedAIIds].includes(item)
        );
        return [...new Set(ai)];
      });
    } else {
      setParentOthers([]);
    }
  }, [selectedTopicIds, discardedAITopics]);

  useEffect(() => {
    if (selectedTopicIds?.some((item) => item.id < -2)) {
      const othersTopics = selectedTopicIds.filter((item) => item.id < -2);
      let childOth = [];
      othersTopics.forEach((item) => {
        const nestedTopics = getAllNestedTopicColsOfParent(
          data?.tree,
          parseInt(item.parent_id, 10)
        );
        const nestedIds = nestedTopics?.map((item) => item?.id);
        const mappingArr = selectedTopicIds?.map(toApiTopic).filter(Boolean);
        const selectedChildIds = mappingArr
          .filter((item) => item.type !== "parent" || item.id < 0)
          ?.map((item) => item?.id);
        childOth = childOth.concat(
          nestedIds.filter((item) => !selectedChildIds.includes(item))
        );
      });
      setChildOthers([...new Set(childOth)]);
    } else {
      setChildOthers([]);
    }
  }, [selectedTopicIds]);

  const handleApplyFilter = (operator) => {
    if (disableTopTopicsBtn || applyDisabled || topTopicsLoading) {
      return;
    }
    if (!data?.tree?.length && selectedTopicIds?.some((t) => t.type === "custom")) {
      // Tree not ready — applying now would drop custom topic ids.
      return;
    }
    const topicsArr = queryFilters?.top_topics || [];
    let newArr = topicsArr
      ?.filter((val) => !val.label)
      ?.concat(selectedTopicIds);
    const mappedArr = (newArr || [])
      .map(toApiTopic)
      .filter((item) => item && item.id != null && !Number.isNaN(Number(item.id)));
    if (showOnlyTree) {
      setTopicsOperator(operator);
      setSavedSearchShowTopTopics(false);
      setSavedSearchTopTopicsFilter(mappedArr);
      setSavedSearchChildOthers(childOthers);
      setSavedSearchParentOthers(parentOthers);
      commitAppliedSnapshot();
      return;
    }
    if (mappedArr.length > 0) {
      params.delete("top_topics");
      params.delete("topics_operator");
      params.delete("child_others");
      params.delete("parent_others");
      // Clear stale pagination cursors so the new filter starts from page 1
      // with the correct date range (allTime or whatever is active).
      ["cursor_next", "cursor_curr", "cursor_before"].forEach((key) =>
        params.delete(key)
      );
      params.append("top_topics", JSON.stringify(mappedArr));
      params.append("topics_operator", operator);
      if (childOthers?.length > 0 || parentOthers?.length > 0) {
        params.append("child_others", JSON.stringify(childOthers));
        params.append("parent_others", JSON.stringify(parentOthers));
      }
      history.push({ search: params.toString() });
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenTopTopic: mappedArr,
          topicsOperator: operator,
        },
      });
    } else {
      params.delete("top_topics");
      params.delete("topics_operator");
      params.delete("child_others");
      params.delete("parent_others");
      ["cursor_next", "cursor_curr", "cursor_before"].forEach((key) =>
        params.delete(key)
      );
      history.push({ search: params.toString() });
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenTopTopic: [],
          topicsOperator: operator,
        },
      });
    }
    commitAppliedSnapshot();
  };

  const commitAppliedSnapshot = () => {
    setAppliedSnapshot({
      source: type,
      view: showHierarchy ? "hierarchy" : "flat",
      sort: sortKey,
      selected: selectedTopicIds?.map((item) => item.id) || [],
      selectedTopics: [...(selectedTopicIds || [])],
    });
    setSelectedFilterType(null);
  };

  const handleDiscardChanges = () => {
    const restoredSelection = [...(appliedSnapshot.selectedTopics || [])];
    setSelectedTopicIds(restoredSelection);
    setCheckedTopicIds(restoredSelection);
    setSelectedFilterType(null);
  };

  const isApplyDisabled =
    Boolean(disableTopTopicsBtn) ||
    Boolean(applyDisabled) ||
    Boolean(topTopicsLoading) ||
    (!data?.tree?.length &&
      selectedTopicIds?.some((topic) => topic.type === "custom"));

  const handlePendingApply = () => {
    if (isApplyDisabled) {
      return;
    }
    if (selectedTopicIds.length === 0) {
      handleApplyFilter("AND");
      return;
    }
    handleApplyFilter(
      selectedTopicIds.some((item) => item.type === "ai") ? "AND" : "OR"
    );
  };

  useEffect(() => {
    if (isApplyDisabled) {
      return;
    }
    if (selectedFilterType && filterTopicCount >= 2) {
      handleApplyFilter(selectedFilterType.value);
    }
  }, [selectedFilterType]);

  const renderApplyControl = (className) => {
    if (!needsOperatorChoice) {
      return (
        <button
          type="button"
          className={className}
          onClick={handlePendingApply}
          disabled={isApplyDisabled}
        >
          {t("button.40")}
        </button>
      );
    }
    return (
      <DropdownSelect
        options={applyOptions}
        selectedOptions={selectedFilterType}
        setSelectedOptions={setSelectedFilterType}
        hideImg={true}
        hiddenTriggerLabel={
          <span className={className}>{t("button.40")}</span>
        }
        triggerEvent={"click"}
        disableMenu={selectedTopicIds.length === 0 || isApplyDisabled}
        placement={"bottomRight"}
        dropdownClassName={showOnlyTree ? styles.topicsOperatorDropdown : ""}
      />
    );
  };

  const isTopicChecked = (item) =>
    checkedTopicIds.some((i) => {
      if (item.type === 1) {
        return Number(i.id) === Number(item.id);
      }
      return Number(i.id) === Number(item.lib_id);
    });

  const getTopicCheckboxId = (item) =>
    item.type === 1 ? item.id : item.lib_id;

  const renderCheckbox = (item) => (
    <input
      ref={item.depth === 0 ? parentBoxRef : null}
      checked={isTopicChecked(item)}
      id={getTopicCheckboxId(item)}
      className={styles.topicCheckBox}
      type="checkbox"
      name={item.name}
      onChange={(e) => {
        const target = e.target;
        const { id, name, value } = target;
        const { itemType, accessKey } = target.dataset;
        handleCheckboxChange({
          ...e,
          target: {
            ...target,
            id,
            name,
            value,
            itemType,
            accessKey,
          },
        });
      }}
      value={item.parent_id}
      onClick={(e) => e.stopPropagation()}
      data-item-type={item.type}
      data-access-key={item.id}
    />
  );

  const toggleTopLevelLeafSelection = (item) => {
    if (disableTopTopicsBtn) {
      return;
    }
    const checkboxId = getTopicCheckboxId(item);
    const checked = isTopicChecked(item);
    handleCheckboxChange({
      target: {
        id: checkboxId,
        name: item.name,
        value: item.parent_id,
        checked: !checked,
        itemType: String(item.type),
        accessKey: String(item.id),
      },
    });
  };

  const renderHierarchyTopLevelLeaf = (item) => {
    const checked = isTopicChecked(item);
    return (
      <div
        key={`top-leaf-${item.lib_id || item.id}`}
        className={`${styles.hierarchyTopLevelLeaf} ${checked ? styles.topicRowSelected : ""}`}
        onClick={() => toggleTopLevelLeafSelection(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleTopLevelLeafSelection(item);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={checked}
      >
        <div className={`${styles.groupTitle} ${styles.groupTitleCompact}`}>
          <div className={styles.groupTitleLeft}>
            <i
              className={`fa-solid fa-chevron-right ${styles.chevronInactive}`}
              aria-hidden
            />
            <span>
              {item.name}
              <span className={styles.groupVolumeInline}>
                {formatCount(item.count || 0)}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderTopicRow = (item, depth = 0, { indent = true } = {}) => {
    const checked = isTopicChecked(item);
    return (
      <div
        key={`${item.lib_id || item.id}-${depth}`}
        className={`${styles.topicRow} ${checked ? styles.topicRowSelected : ""}`}
        style={indent ? { paddingLeft: 12 + depth * 20 } : undefined}
      >
        {renderCheckbox({ ...item, depth })}
        <span className={styles.topicName} title={item.name}>
          {item.name}
        </span>
        <span className={styles.topicVolume}>
          {formatCount(item.count || 0)}
        </span>
      </div>
    );
  };

  const getNestedChild = (items) => {
    return items?.map((it) => (
      <div key={it.lib_id} className={styles.hierarchyNest}>
        {it.child?.length > 0 ? (
          <Collapse
            className={showOnlyTree ? styles.boxColorCollapse : styles.collapse}
            bordered={false}
            accordion
          >
            <Collapse.Panel
              key={it.lib_id}
              showArrow={false}
              header={
                <div className={styles.groupTitle}>
                  <div className={styles.groupTitleLeft}>
                    <i className="fa-solid fa-chevron-right" />
                    <span>
                      {it.name}
                      <span className={styles.groupCount}>({it.child.length})</span>
                    </span>
                  </div>
                  <div className={styles.groupMeta}>
                    <span className={styles.groupVolume}>
                      {formatCount(it.count || 0)}
                    </span>
                  </div>
                </div>
              }
              collapsible="header"
            >
              {renderTopicRow(it, 0, { indent: false })}
              {getNestedChild(it.child)}
            </Collapse.Panel>
          </Collapse>
        ) : (
          renderTopicRow(it, 0, { indent: false })
        )}
      </div>
    ));
  };

  const renderCategoryTable = () => (
    <div className={styles.categoryBlock}>
      <div className={styles.controlsRow}>
        <div className={styles.searchSortRow}>
          <Input
            className={styles.searchInput}
            type="search"
            placeholder={t("top_topics.9")}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            allowClear
          />
          <Select
            className={styles.sortSelect}
            value={sortKey}
            onChange={(value) => {
              setSortKey(value);
            }}
            options={[
              { value: "volume", label: t("top_topics.10") },
              { value: "alpha", label: t("top_topics.11") },
            ]}
          />
        </div>
      </div>

      <div className={styles.topicTable}>
        <div className={styles.topicTableHeader}>
          <span />
          <span>{t("top_topics.6")}</span>
          <span>{t("top_topics.8")}</span>
        </div>
        <Spin spinning={topTopicsLoading}>
          {visibleCategoryList?.length !== 0 ? (
            showHierarchy ? (
              <div className={styles.hierarchyList}>
                {visibleCategoryList.map((item) =>
                  item.child?.length > 0 ? (
                    <Collapse
                      key={item.lib_id}
                      className={
                        showOnlyTree ? styles.boxColorCollapse : styles.collapse
                      }
                      bordered={false}
                      accordion
                    >
                      <Collapse.Panel
                        key={item.lib_id}
                        showArrow={false}
                        header={
                          <div className={styles.groupTitle}>
                            <div className={styles.groupTitleLeft}>
                              <i className="fa-solid fa-chevron-right" />
                              <span>
                                {item.name}
                                <span className={styles.groupCount}>
                                  ({item.child.length})
                                </span>
                              </span>
                            </div>
                            <div className={styles.groupMeta}>
                              <span className={styles.groupVolume}>
                                {formatCount(item.count || 0)}
                              </span>
                            </div>
                          </div>
                        }
                        collapsible="header"
                      >
                        <div className={styles.hierarchyPanelBody}>
                          {renderTopicRow(item, 0, { indent: false })}
                          {getNestedChild(item.child)}
                        </div>
                      </Collapse.Panel>
                    </Collapse>
                  ) : (
                    renderHierarchyTopLevelLeaf(item)
                  )
                )}
              </div>
            ) : (
              flattenTopics(visibleCategoryList).map((item) =>
                renderTopicRow(item, item.depth)
              )
            )
          ) : (
            <p className={styles.emptyTopics}>{t("notifications.80")}</p>
          )}
        </Spin>
      </div>
    </div>
  );

  return (
    <div
      className={
        applyPopupStyling ? styles.popupHotWordsSection : styles.HotWordsSection
      }
    >
      <div className={styles.visualTerms}>
        {words?.length === 0 && !topTopicsLoading && viewType === "plain" ? (
          <div className={styles.emptyState}>
            <div className={styles.title}>
              <img
                src={
                  theme === "dark"
                    ? "/assets/emptyStates/topic_library_icon.svg"
                    : "/assets/emptyStates/topic_library_black.svg"
                }
                alt=""
              />
              {t("notifications.80")}
            </div>
            <div className={styles.description}>
              To create custom topics, go{" "}
              <Link to="/settings/topic-library">Topic Library</Link>
            </div>
          </div>
        ) : viewType === "plain" ? (
          <div className={styles.topTopicsPlain}>{visualWords}</div>
        ) : (
          renderCategoryTable()
        )}
      </div>

      {hasSelectionPending && !disableTopTopicsBtn ? (
        <div className={styles.stickyApplyBar}>
          <span className={`${styles.chip} ${styles.chipActive}`}>
            {filterTopicCount}
          </span>
          <span className={styles.stickyText}>{selectionPendingMessage}</span>
          <div className={styles.stickyActions}>
            <Button
              type="text"
              className={styles.ghostBtn}
              onClick={handleDiscardChanges}
            >
              {t("top_topics.21")}
            </Button>
            {renderApplyControl(styles.applyBtn)}
          </div>
        </div>
      ) : null}

      {words?.length > 10 && viewType === "plain" ? (
        <div className={styles.footer}>
          <div className={styles.btn}>
            <Button
              className={styles.button}
              disabled={startIndex === 0}
              onClick={() => updatePage(-1)}
            >
              <i className="fas fa-chevron-left"></i>
            </Button>
          </div>
          <div className={styles.btn}>
            <Button
              className={styles.button}
              disabled={
                (words?.length % 10 === 0 &&
                  words?.length / 10 === startIndex + 1) ||
                (words?.length % 10 !== 0 &&
                  Math.trunc(words?.length / 10) + 1 === startIndex + 1)
              }
              onClick={() => updatePage(1)}
            >
              <i className="fas fa-chevron-right"></i>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TopTopics;
